import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux/es/exports";
import { useLocation, useNavigate } from "react-router-dom";
import { APP_URL } from "../../../constants";
import {
  getStaffInformation,
  setAppId,
  updateStaffPersonalInfo,
} from "../../../redux/slice/staff/staffSlice";
import useFileSelected from "../../../utils/hooks/useFileSelected";
import Form from "../../form/Form";
import InputDate from "../../form/inputs/datePicker";
import { fetchAllStates } from "./../../../redux/slice/staff/staffSlice";
import useInputChange from "./../../../utils/hooks/useInputChange";
import { isEmptyObject } from "./../../../utils/validators/validators";
import moment from "moment";
import { convertDate } from "../../../utils/helpers/filter";

const PersonalInfo = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const passportRef = useRef();

  const [hasEditStaff, setHasEditStaff] = useState();
  const [personalInfo, setPersonalInfo] = useState({});
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState({});
  const [localgovernment, setLocalgovernment] = useState([]);
  const { personal_information, status } = useSelector(
    (state) => state?.staff.staff_information
  );
  const { user_permissions } = useSelector((state) => state.auth?.user);

  // fetch records from redux state
  const { allStates } = useSelector((state) => state.staff);

  // initialize input fields
  const [staffInputData, setStaffInputData] = useState({
    first_name: "",
    last_name: "",
    middle_name: "",
    state: "",
    l_g_a: "",
    religion: "",
    gender: "",
    d_o_b: "",
    marital_status: "",
    language_spoken: "",
    image: {},
    temp_url: "",
  });

  // Custom hook for input change
  const [input, handleChange] = useInputChange(
    staffInputData,
    setStaffInputData,
    error,
    setError
  );

  // Custom hook for file change
  const [fileInput, fileChangeHandler] = useFileSelected(
    staffInputData,
    setStaffInputData,
    error,
    setError
  );

  const { staff_id } = location?.state;

  // Update Personal Info
  const handleSubmit = () => {
    if (isEmptyObject(error)) {
      dispatch(updateStaffPersonalInfo(staffInputData));
    }
  };

  // Continue Registration Controller
  const continueRegistrationHandler = (staffId) => {
    dispatch(setAppId(staffId));
    navigate("/admin/add-staff", { state: { from: location }, replace: true });
  };

  useEffect(() => {
    setPersonalInfo(personal_information);
    if (personalInfo) {
      setStaffInputData({
        ...staffInputData,
        first_name: personalInfo?.first_name,
        last_name: personalInfo?.last_name,
        middle_name: personalInfo?.middle_name,
        state: personalInfo?.state,
        l_g_a: personalInfo?.l_g_a,
        religion: personalInfo?.religion,
        gender: personalInfo?.gender,
        d_o_b: personalInfo?.d_o_b,
        marital_status: personalInfo?.marital_status,
        language_spoken: personalInfo?.language_spoken,
        id: personalInfo?.id,
      });
    }
  }, [personal_information, editing, personalInfo]);

  useEffect(() => {
    setHasEditStaff(
      user_permissions?.some((permission) => permission.name === "edit staff")
    );
  }, [user_permissions]);

  return (
    <div className='border p-4 rounded-3 my-1 px-3 position-relative'>
      {status?.status === "pending" && (
        <div className='alert alert-warning'>
          <div className='d-flex justify-content-between'>
            <div className='col-xs-12'>
              <h5>Registration not complete </h5>
            </div>
            <span
              className='btn btn-sm btn-info ms-2'
              onClick={() => continueRegistrationHandler(staff_id)}
            >
              Continue Registration
            </span>
          </div>
        </div>
      )}
      <h4 className='fw-bold text-primary bg-white position-absolute profile-title'>
        Personal Information
      </h4>
      <Form
        setError={setError}
        submitHandler={handleSubmit}
        values={staffInputData}
      >
        <div className='row'>
          <div className='col-sm-12 col-md-8'>
            <div className='row mb-2 py-2 bg-bod shadow-lg'>
              <div className='col-xs-12 col-sm-3 col-md-3 px-3 '>
                <h5 className='text-whit'>First Name</h5>
                {!editing && (
                  <p className='text-whie-50'>{personalInfo?.first_name}</p>
                )}
                {editing && (
                  <input
                    min='3'
                    textname='first name'
                    className={`form-control update-input ${
                      error.first_name && "is-invalid"
                    }`}
                    type='text'
                    name='first_name'
                    value={input.first_name}
                    onChange={handleChange}
                    id='first_name'
                  />
                )}
                {error.first_name && (
                  <span className='invalid-feedback'>{error.first_name}</span>
                )}
              </div>
              <div className='col-xs-12 col-sm-3 col-md-3 px-3'>
                <h5 className='text-whte'>Last Name</h5>
                {!editing && (
                  <p className='text-whte-50'>{personalInfo?.last_name}</p>
                )}
                {editing && (
                  <input
                    min='3'
                    textname='last name'
                    className={`form-control update-input ${
                      error.last_name && "is-invalid"
                    }`}
                    value={input.last_name}
                    name='last_name'
                    onChange={handleChange}
                    id='last_name'
                  />
                )}
                {error.last_name && (
                  <span className='invalid-feedback'>{error.last_name}</span>
                )}
              </div>
              <div className='col-xs-12 col-sm-3 col-md-3 px-3'>
                <h5 className='text-whit'>Middle Name</h5>
                {!editing && (
                  <p className='text-whit-50'>{personalInfo?.middle_name}</p>
                )}
                {editing && (
                  <input
                    min='3'
                    validate='false'
                    textname='middle name'
                    className={`form-control update-input ${
                      error.middle_name && "is-invalid"
                    }`}
                    type='text'
                    value={input.middle_name}
                    name='middle_name'
                    onChange={handleChange}
                    id='middle_name'
                  />
                )}
                {error.middle_name && (
                  <span className='invalid-feedback'>{error.middle_name}</span>
                )}
              </div>
              <div className='col-xs-12 col-sm-3 col-md-3 px-3'>
                <h5 className='text-whit'>Gender</h5>
                {!editing && (
                  <p className='text-white-5'>{personalInfo?.gender}</p>
                )}
                {editing && (
                  <input
                    textname='gender'
                    className={`form-control update-input ${
                      error.gender && "is-invalid"
                    }`}
                    type='text'
                    value={input.gender}
                    name='gender'
                    onChange={handleChange}
                    id='gender'
                  />
                )}
                {error.gender && (
                  <span className='invalid-feedback'>{error.gender}</span>
                )}
              </div>
            </div>
            {/* <hr /> */}
            <div className='row  mb-2 py-2 bg-bod shadow-lg'>
              {/* style={{background: '#4d4e62'}} */}
              <div className='col-xs-6 col-sm-3 col-md-3 px-3'>
                <h5 className='text-whie'>Date of Birth</h5>
                {!editing && (
                  <p className='text-whit-50'>{personalInfo?.d_o_b}</p>
                )}
                {editing && (
                  <InputDate
                    name='d_o_b'
                    textname='date of birth'
                    className={`form-select ${error.d_o_b && "is-invalid"}`}
                    state={staffInputData}
                    setState={setStaffInputData}
                    error={error}
                    setError={setError}
                    defaultDate={staffInputData?.d_o_b}
                  />
                )}
                {error.gender && (
                  <span className='invalid-feedback'>{error.gender}</span>
                )}
              </div>
              <div className=' col-xs-6 col-sm-3 col-md-3 px-3'>
                <h5 className='text-whte'>State</h5>
                {!editing && (
                  <p className='text-whte-50'>{personalInfo?.state}</p>
                )}
                {editing && (
                  <select
                    textname='state'
                    name='state'
                    value={input.state}
                    id='state'
                    className={`form-select ${error.state && "is-invalid"}`}
                    onChange={(e) => {
                      setStaffInputData({
                        ...staffInputData,
                        state: e.target.value,
                      });
                      setError((current) => {
                        const copy = { ...current };
                        delete copy["state"];
                        return copy;
                      });
                      setLocalgovernment(() => {
                        return allStates.filter((state) => {
                          return state.name === e.target.value;
                        });
                      });
                    }}
                  >
                    <option value=''>Choose state</option>
                    {allStates &&
                      allStates.map(({ id, name }) => (
                        <option key={id} value={name}>
                          {name}
                        </option>
                      ))}
                  </select>
                )}
                {error.state && (
                  <span className='invalid-feedback'>{error.state}</span>
                )}
              </div>
              <div className='col-xs-6 col-sm-3 col-md-3 px-3'>
                <h5 className='text-whie'>L.G.A</h5>
                {!editing && (
                  <p className='text-whie-50'>{personalInfo?.l_g_a}</p>
                )}
                {editing && (
                  <select
                    textname='L.G.A'
                    name='l_g_a'
                    id='l_g_a'
                    value={input.l_g_a}
                    onChange={handleChange}
                    className={`form-select px-  ${
                      error.l_g_a && "is-invalid"
                    }`}
                    disabled={localgovernment?.length === 0 ? true : false}
                  >
                    <option value={input.l_g_a}>{input.l_g_a}</option>
                    {localgovernment[0]?.lga.map(({ id, name }) => (
                      <option key={id} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                )}
                {error.l_g_a && (
                  <span className='invalid-feedback'>{error.l_g_a}</span>
                )}
              </div>
              <div className='col-xs-6 col-sm-3 col-md-3 px-3'>
                <h5 className='text-whte'>Marital Status</h5>
                {!editing && (
                  <p className='text-whte-50'>{personalInfo?.marital_status}</p>
                )}
                {editing && (
                  <select
                    textname='marital status'
                    name='marital_status'
                    id='marita_status'
                    value={input.marital_status}
                    onChange={handleChange}
                    className={`form-select ${
                      error.marital_status && "is-invalid"
                    }`}
                  >
                    <option value='Single'>Single</option>
                    <option value='Married'>Married</option>
                    <option value='Divorced'>Divorced</option>
                  </select>
                )}
                {error.middle_name && (
                  <span className='invalid-feedback'>{error.middle_name}</span>
                )}
              </div>
            </div>
            {/* <hr /> */}
            <div className='row mb- py-2 bg-bod shadow-lg'>
              <div className='col-sm-3 col-md-6 px-3'>
                <h5>Language(s) spoken</h5>
                {!editing && <p>{personalInfo?.language_spoken}</p>}
                {editing && (
                  <input
                    className={`form-control update-input ${
                      error.language_spoken && "is-invalid"
                    }`}
                    min='10'
                    validate='false'
                    textname='language spoken'
                    type='text'
                    value={input.language_spoken}
                    name='language_spoken'
                    onChange={handleChange}
                    id='language_spoken'
                  />
                )}
                {error.language_spoken && (
                  <span className='invalid-feedback'>
                    {error.language_spoken}
                  </span>
                )}
              </div>
              <div className='col-xs-6 col-sm-3 col-md-6 px-3'>
                <h5>Religion</h5>
                {!editing && <p>{personalInfo?.religion}</p>}
                {editing && (
                  <select
                    textname='religion'
                    name='religion'
                    id='religion'
                    value={input.religion}
                    onChange={handleChange}
                    className={`form-select update-input ${
                      error.religion && "is-invalid"
                    }`}
                  >
                    <option value=''>Choose religion</option>
                    <option value='Christainity'>Christainity</option>
                    <option value='Islam'>Islam</option>
                    <option value='Traditional'>Traditional</option>
                    <option value='Others'>Others</option>
                  </select>
                )}
                {error.religion && (
                  <span className='invalid-feedback'>{error.religion}</span>
                )}
              </div>
            </div>
          </div>

          <div className='col-sm-12 col-md-4 '>
            <div className='profile-picture-container bg-bod hadow-lg'>
              <div>
                <div className='d-flex mt-2 justify-content-cente'>
                  <input
                    textname='passport'
                    className='d-none'
                    accept='image/*'
                    type='file'
                    name='image'
                    // value={input?.image}
                    onChange={fileChangeHandler}
                    ref={passportRef}
                  />
                  <img
                    className='profile-picture mx-auto img-responsive'
                    src={`${
                      (editing &&
                        staffInputData.temp_url &&
                        staffInputData.temp_url) ||
                      personalInfo?.image?.includes("res.cloudinary")
                        ? personalInfo?.image
                        : APP_URL + personalInfo?.image
                    }`}
                    alt='profile'
                  />
                </div>
                <div className='d-flex justify-content-center'>
                  <div>
                    {editing && (
                      <span
                        className='btn btn-sm btn-secondary mt-2 px-3'
                        onClick={() => passportRef.current.click()}
                      >
                        Browse
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {hasEditStaff && (
            <div className='row'>
              <div className='d-flex justify-content-end'>
                <div>
                  <button
                    type='button'
                    className={`btn btn-${
                      editing ? "warning" : "info"
                    } btn-sm mt-3 px-4 text-white`}
                    onClick={() => {
                      setEditing(editing ? false : true);
                      // setError({...{}, })
                    }}
                  >
                    {editing ? "Close" : "Edit"}
                  </button>
                  {editing && (
                    <button className='btn btn-success ms-2 btn-sm mt-3 px-4'>
                      Save
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Form>
    </div>
  );
};

export default PersonalInfo;
